import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

/**
 * useJsonLd — injects a JSON-LD schema.org <script> tag into document.head,
 * scoped to the component lifecycle. Removes on unmount to prevent stale
 * markup on the client-side router.
 *
 * Usage:
 *   useJsonLd({ "@context": "https://schema.org", "@type": "Article", ... });
 */
export function useJsonLd(data, key = "default") {
  useEffect(() => {
    if (!data) return;
    const id = `jsonld-${key}`;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const stale = document.getElementById(id);
      if (stale) stale.remove();
    };
  }, [JSON.stringify(data), key]);
}

/**
 * useMeta — updates document.title + meta description + og/twitter tags.
 * Restores previous title on unmount.
 */
export function useMeta({ title, description, image, url } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [key, val] = selector.replace(/[[\]"]/g, "").split("=");
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:image"]', "content", image);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:card"]', "content", image ? "summary_large_image" : "summary");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);
    return () => { document.title = prevTitle; };
  }, [title, description, image, url]);
}

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Rescigo Health",
  legalName: "Rescigo Health Private Limited",
  url: "https://rescigo.in",
  logo: "https://rescigo.in/brand/rescigo-logo.png",
  description:
    "India's enterprise-grade waste intelligence, compliance, and public-health infrastructure platform for governments, enterprises, and healthcare facilities.",
  foundingDate: "2023",
  areaServed: { "@type": "Country", name: "India" },
  sameAs: [
    "https://www.linkedin.com/company/rescigo-health",
    "https://twitter.com/rescigohealth",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "care@rescigo.in",
      telephone: "+91-73-0608-7704",
      availableLanguage: ["English", "Malayalam", "Hindi"],
    },
    {
      "@type": "ContactPoint",
      contactType: "investor relations",
      email: "ceo@rescigo.in",
    },
    {
      "@type": "ContactPoint",
      contactType: "security",
      email: "care@rescigo.in",
    },
  ],
  address: {
    "@type": "PostalAddress",
    addressCountry: "IN",
    addressRegion: "Kerala",
    addressLocality: "Kochi",
  },
};

export function publicationSchema(p, fullUrl) {
  if (!p) return null;
  const isReport = p.kind === "white_paper" || p.kind === "research" || p.kind === "csr_report";
  const type = isReport ? "Report" : "BlogPosting";
  return {
    "@context": "https://schema.org",
    "@type": type,
    headline: p.seo_title || p.title,
    alternativeHeadline: p.subtitle || undefined,
    description: p.seo_description || p.summary,
    image: p.cover_url || undefined,
    datePublished: p.published_at,
    dateModified: p.updated_at || p.published_at,
    author: p.author
      ? { "@type": "Person", name: p.author }
      : { "@type": "Organization", name: "Rescigo Health" },
    publisher: {
      "@type": "Organization",
      name: "Rescigo Health",
      logo: {
        "@type": "ImageObject",
        url: "https://rescigo.in/brand/rescigo-logo.png",
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": fullUrl,
    },
    keywords: p.seo_keywords || p.category,
    articleSection: p.category || undefined,
    ...(isReport && p.pdf_url ? { url: p.pdf_url } : {}),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((i, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: i.name,
      item: i.url,
    })),
  };
}

export function collectionSchema({ name, description, url, items }) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description,
    url,
    hasPart: (items || []).slice(0, 20).map(p => ({
      "@type": p.kind === "newsroom" ? "BlogPosting" : "Report",
      headline: p.title,
      description: p.summary,
      datePublished: p.published_at,
      url: `${url}/${p.slug}`,
      image: p.cover_url || undefined,
    })),
  };
}

// ============================================================
// Helmet-based <SEO/> component (used by dynamic landing pages)
// ============================================================
export const PROD_ORIGIN = "https://www.rescigo.in";

const DEFAULTS = {
  siteName: "Rescigo Health",
  defaultTitle: "Rescigo Health · Biomedical waste collection & Smart civic services",
  defaultDescription:
    "Rescigo delivers CPCB-compliant biomedical waste collection, sanitary-napkin & diaper disposal, expired-medicine pickup and health services across Kerala — Trivandrum, Kochi, Kottayam and 12+ cities.",
  defaultImage: `${PROD_ORIGIN}/brand/og-cover.png`,
  twitter: "@rescigo",
};

export default function SEO({
  title,
  description,
  path = "/",
  image,
  noIndex = false,
  keywords,
  jsonLd,
  breadcrumbs,
}) {
  const fullTitle = title
    ? `${title} · ${DEFAULTS.siteName}`
    : DEFAULTS.defaultTitle;
  const desc = description || DEFAULTS.defaultDescription;
  const url = `${PROD_ORIGIN}${path}`;
  const img = image || DEFAULTS.defaultImage;

  const scripts = [];
  if (breadcrumbs && breadcrumbs.length) {
    scripts.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: `${PROD_ORIGIN}${b.path}`,
      })),
    });
  }
  if (jsonLd) {
    if (Array.isArray(jsonLd)) scripts.push(...jsonLd);
    else scripts.push(jsonLd);
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc}/>
      {keywords && <meta name="keywords" content={keywords}/>}
      <link rel="canonical" href={url}/>
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow"/>
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"/>
      )}

      <meta property="og:type" content="website"/>
      <meta property="og:site_name" content={DEFAULTS.siteName}/>
      <meta property="og:title" content={fullTitle}/>
      <meta property="og:description" content={desc}/>
      <meta property="og:url" content={url}/>
      <meta property="og:image" content={img}/>
      <meta property="og:locale" content="en_IN"/>

      <meta name="twitter:card" content="summary_large_image"/>
      <meta name="twitter:site" content={DEFAULTS.twitter}/>
      <meta name="twitter:title" content={fullTitle}/>
      <meta name="twitter:description" content={desc}/>
      <meta name="twitter:image" content={img}/>

      {scripts.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

// Branch directory for LocalBusiness JSON-LD
export const CITY_BRANCHES = [
  {
    slug: "trivandrum",
    name: "Rescigo Health — Trivandrum",
    city: "Thiruvananthapuram",
    aliases: ["Trivandrum", "Thiruvananthapuram", "TVM"],
    region: "Kerala",
    postal: "695001",
    street: "Rescigo Head Office, Trivandrum",
    phone: "+91-70259-67117",
    email: "hello@rescigo.in",
    lat: 8.5241, lng: 76.9366,
  },
  {
    slug: "kochi",
    name: "Rescigo Health — Kochi",
    city: "Kochi",
    aliases: ["Kochi", "Ernakulam", "Cochin"],
    region: "Kerala",
    postal: "682001",
    street: "Rescigo Kochi Branch",
    phone: "+91-70259-67117",
    email: "kochi@rescigo.in",
    lat: 9.9312, lng: 76.2673,
  },
  {
    slug: "kottayam",
    name: "Rescigo Health — Kottayam",
    city: "Kottayam",
    aliases: ["Kottayam"],
    region: "Kerala",
    postal: "686001",
    street: "Rescigo Kottayam Branch",
    phone: "+91-70259-67117",
    email: "kottayam@rescigo.in",
    lat: 9.5916, lng: 76.5222,
  },
];

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${PROD_ORIGIN}/#organization`,
    "name": "Rescigo Health",
    "url": PROD_ORIGIN,
    "logo": `${PROD_ORIGIN}/brand/rescigo-logo.png`,
    "description":
      "Rescigo — Kerala's leading biomedical, sanitary and diaper waste collection service for homes, healthcare institutions and civic bodies. CPCB/BMW 2016 compliant.",
    "sameAs": [
      "https://www.facebook.com/rescigo",
      "https://www.instagram.com/rescigo",
      "https://www.linkedin.com/company/rescigo",
    ],
    "contactPoint": [{
      "@type": "ContactPoint",
      "telephone": "+91-70259-67117",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": ["English", "Malayalam", "Hindi"],
    }],
    "founder": {
      "@type": "Person",
      "name": "Lekshmi Manjunath",
    },
  };
}

export function localBusinessSchema(branch) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${PROD_ORIGIN}/branches/${branch.slug}#business`,
    "name": branch.name,
    "image": `${PROD_ORIGIN}/brand/og-cover.png`,
    "url": `${PROD_ORIGIN}/services/biomedical-waste-collection/${branch.slug}`,
    "telephone": branch.phone,
    "email": branch.email,
    "priceRange": "\u20B9\u20B9",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": branch.street,
      "addressLocality": branch.city,
      "addressRegion": branch.region,
      "postalCode": branch.postal,
      "addressCountry": "IN",
    },
    "geo": { "@type": "GeoCoordinates",
              "latitude": branch.lat, "longitude": branch.lng },
    "areaServed": [branch.city, branch.region, "India"],
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
      "opens": "08:00", "closes": "20:00",
    }],
  };
}

export function serviceSchema({ name, description, path, areaServed, provider }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "url": `${PROD_ORIGIN}${path}`,
    "provider": { "@type": "Organization", "name": provider || "Rescigo Health",
                   "url": PROD_ORIGIN },
    "areaServed": Array.isArray(areaServed) ? areaServed.map(a =>
      ({ "@type": "AdministrativeArea", "name": a })) : areaServed,
    "serviceType": name,
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
}
